import {
  createContext,
  useContext as useAuthContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useClinician } from "../../hooks/queries/useClinician";
import {
  completeOnboarding as completeOnboardingService,
  createClinician,
  updateClinician,
} from "../../services/ClinicianService";
import { changeTheme } from "../../store/slices/ApplicationSlice";
import { setCulture, setLang } from "../../store/slices/i18nSlice";
import { AuthContext } from "./AuthContext";

const UserContext = createContext();

// Client-only date defaults for now. Flip the flag when backend preferences
// support storing locale/format.
const ENABLE_SERVER_DATE_SETTINGS = false;
const DEFAULT_DATE_SETTINGS = {
  locale: "en-US",
  format: "MM/dd/yyyy",
};

const DEFAULT_PREFERENCES = {
  theme: "dark",
  language: "EN",
  display_settings: {
    length_unit: "cm",
    weight_unit: "kg",
    timezone: "UTC",
    float_sensitivity: 1.0,
  },
  spino_pelvic_parameters: [14003, 14001, 14002, 14004, 14027, 14017, 14032, 14013, 14014, 14043],
};

const normalizeDisplaySettings = (displaySettings = {}) => {
  const normalized = {
    length_unit: displaySettings.length_unit ?? "cm",
    weight_unit: displaySettings.weight_unit ?? "kg",
    timezone: displaySettings.timezone ?? "UTC",
    float_sensitivity: displaySettings.float_sensitivity ?? 1.0,
  };

  if (ENABLE_SERVER_DATE_SETTINGS) {
    normalized.date_locale =
      displaySettings.date_locale ?? DEFAULT_DATE_SETTINGS.locale;
    normalized.date_format =
      displaySettings.date_format ?? DEFAULT_DATE_SETTINGS.format;
  }

  return normalized;
};

const normalizePreferences = preferences => {
  if (!preferences) {
    return null;
  }

  return {
    ...preferences,
    // Normalize language code to uppercase for consistency with translation keys
    language: preferences.language?.toUpperCase() ?? "EN",
    display_settings: normalizeDisplaySettings(preferences.display_settings),
    spino_pelvic_parameters: Array.isArray(preferences.spino_pelvic_parameters)
      ? preferences.spino_pelvic_parameters
      : [],
  };
};

export const UserProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [userPreferences, setUserPreferences] = useState(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState(null);
  const [isCreatingClinician, setIsCreatingClinician] = useState(false);
  const [hasAppliedToRedux, setHasAppliedToRedux] = useState(false);

  const hasTriedCreation = useRef(false);

  // Get auth context
  const authContext = useAuthContext(AuthContext);
  const {
    user: authUser,
    isAuthenticated,
    loading: authLoading,
    claims,
  } = authContext || {};

  // Always try to load clinician (which includes preferences)
  const { clinician, loading, error, refreshClinician } = useClinician();
  const clinicianId = clinician?._id;

  // Sync user with auth context
  useEffect(() => {
    if (!authLoading) {
      const newUser = isAuthenticated && authUser ? authUser : null;
      setUser(newUser);

      // Reset when user changes
      if (!newUser) {
        hasTriedCreation.current = false;
        setUserPreferences(null);
        setOnboardingCompleted(null);
      }
    }
  }, [authUser, isAuthenticated, authLoading]);

  const buildDefaultClinicianPayload = useCallback(() => {
    const firstname = claims?.firstName?.trim() || "";
    const lastname = claims?.lastName?.trim() || "";
    // Username is usually an email; fall back to empty if not present
    const email =
      typeof claims?.username === "string" && claims.username.includes("@")
        ? claims.username
        : "";

    return {
      firstname,
      lastname,
      email,
      preferences: DEFAULT_PREFERENCES,
      onboarding_completed: false,
    };
  }, [claims]);

  // Create default clinician with preferences
  const createDefaultClinician = useCallback(async () => {
    if (isCreatingClinician || !user || hasTriedCreation.current) {
      return;
    }

    try {
      hasTriedCreation.current = true;
      setIsCreatingClinician(true);

      await createClinician(buildDefaultClinicianPayload());
      await refreshClinician();
    } catch (error) {
      setUserPreferences(normalizePreferences(DEFAULT_PREFERENCES));
      setOnboardingCompleted(false);
    } finally {
      setIsCreatingClinician(false);
    }
  }, [
    isCreatingClinician,
    user,
    refreshClinician,
    buildDefaultClinicianPayload,
  ]);

  // Simple clinician/preference logic
  useEffect(() => {
    // Skip if still loading
    if (authLoading || loading || isCreatingClinician) {
      return;
    }


    if (clinician) {
      // Found clinician in DB - extract preferences
      setUserPreferences(normalizePreferences(clinician.preferences));
      setOnboardingCompleted(clinician.onboarding_completed ?? false);
    } else if (user && !error) {
      // User exists but no clinician - create one
      createDefaultClinician();
    } else if (!user) {
      // No user - clear state
      setUserPreferences(null);
      setOnboardingCompleted(null);
      setHasAppliedToRedux(false);
      hasTriedCreation.current = false;
    } else if (error) {
      // Error loading - use defaults
      console.error("[UserContext] Error loading clinician:", error);
      setUserPreferences(normalizePreferences(DEFAULT_PREFERENCES));
      setOnboardingCompleted(false);
    }
  }, [
    clinician,
    loading,
    error,
    user,
    authLoading,
    isCreatingClinician,
    createDefaultClinician,
  ]);

  // Apply preferences to Redux when ready
  useEffect(() => {
    if (userPreferences && !hasAppliedToRedux && !authLoading && !loading) {
      // Apply theme
      if (userPreferences.theme) {
        dispatch(changeTheme(userPreferences.theme));
      }

      // Apply language
      if (userPreferences.language) {
        dispatch(setLang(userPreferences.language));

        const supportedCultures = {
          EN: "en-US",
          FR: "fr-FR",
        };

        if (supportedCultures[userPreferences.language]) {
          dispatch(setCulture(supportedCultures[userPreferences.language]));
        }
      }

      setHasAppliedToRedux(true);
    }
  }, [userPreferences, hasAppliedToRedux, authLoading, loading, dispatch]);

  // Function to complete onboarding
  const completeOnboarding = useCallback(async () => {

    // Guard against calling this when clinician isn't ready
    if (!clinicianId) {
      console.error(
        "completeOnboarding called but clinicianId is not available"
      );
      return;
    }

    if (isCreatingClinician || loading) {
      console.error(
        "completeOnboarding called while clinician is still loading/creating"
      );
      return;
    }

    try {
      await completeOnboardingService(clinicianId);
      setOnboardingCompleted(true);
      await refreshClinician();
    } catch (error) {
      console.error("Error completing onboarding:", error);
      // Still set local state to true to not block the user
      setOnboardingCompleted(true);
    }
  }, [refreshClinician, clinicianId, isCreatingClinician, loading]);

  // Functions to update preferences (now updates clinician.preferences)
  const updateUserTheme = useCallback(
    async newTheme => {
      if (!userPreferences) {
        return;
      }

      // Update Redux immediately
      dispatch(changeTheme(newTheme));

      // Update local state
      const updatedPreferences = normalizePreferences({
        ...userPreferences,
        theme: newTheme,
      });
      setUserPreferences(updatedPreferences);

      // Save to server via clinician endpoint
      if (!clinicianId) {
        return;
      }
      try {
        await updateClinician(
          { preferences: { theme: newTheme } },
          clinicianId
        );
      } catch (error) {}
    },
    [userPreferences, dispatch, clinicianId]
  );

  const updateUserLanguage = useCallback(
    async newLanguage => {
      dispatch(setLang(newLanguage));

      const supportedCultures = {
        EN: "en-US",
        FR: "fr-FR",
      };

      if (supportedCultures[newLanguage]) {
        dispatch(setCulture(supportedCultures[newLanguage]));
      }

      if (!userPreferences) {
        return;
      }

      const updatedPreferences = normalizePreferences({
        ...userPreferences,
        language: newLanguage,
      });
      setUserPreferences(updatedPreferences);

      // Save to server via clinician endpoint
      if (!clinicianId) {
        return;
      }
      try {
        await updateClinician(
          { preferences: { language: newLanguage } },
          clinicianId
        );
      } catch (error) {}
    },
    [userPreferences, dispatch, clinicianId]
  );

  const updateDisplaySettings = useCallback(
    async (updates, logLabel = "display settings") => {
      if (!userPreferences) {
        return;
      }

      const mergedDisplaySettings = normalizeDisplaySettings({
        ...userPreferences?.display_settings,
        ...updates,
      });

      const updatedPreferences = normalizePreferences({
        ...userPreferences,
        display_settings: mergedDisplaySettings,
      });
      setUserPreferences(updatedPreferences);

      if (!clinicianId) {
        return;
      }

      try {
        await updateClinician(
          {
            preferences: {
              display_settings: mergedDisplaySettings,
            },
          },
          clinicianId
        );
      } catch (error) {}
    },
    [userPreferences, clinicianId]
  );

  const updateUserTimezone = useCallback(
    async newTimezone => {
      await updateDisplaySettings({ timezone: newTimezone }, "Timezone");
    },
    [updateDisplaySettings]
  );

  const updateUserLengthUnit = useCallback(
    async newLengthUnit => {
      await updateDisplaySettings(
        { length_unit: newLengthUnit },
        "Length unit"
      );
    },
    [updateDisplaySettings]
  );

  const updateUserWeightUnit = useCallback(
    async newWeightUnit => {
      await updateDisplaySettings(
        { weight_unit: newWeightUnit },
        "Weight unit"
      );
    },
    [updateDisplaySettings]
  );

  const updateFloatSensitivity = useCallback(
    async newSensitivity => {
      await updateDisplaySettings(
        { float_sensitivity: newSensitivity },
        "Float sensitivity"
      );
    },
    [updateDisplaySettings]
  );

  const updateKeyParameters = useCallback(
    async parameterKeys => {
      if (!userPreferences) {
        return;
      }

      const normalizedKeys = Array.isArray(parameterKeys)
        ? parameterKeys.map(key => Number(key)).filter(Number.isFinite)
        : [];

      const updatedPreferences = normalizePreferences({
        ...userPreferences,
        spino_pelvic_parameters: normalizedKeys,
      });
      setUserPreferences(updatedPreferences);

      if (!clinicianId) {
        return;
      }

      try {
        await updateClinician(
          {
            preferences: {
              spino_pelvic_parameters: normalizedKeys,
            },
          },
          clinicianId
        );
      } catch (error) {}
    },
    [userPreferences, clinicianId]
  );

  // Clinician is ready when we have a valid clinician with an ID and we're not in the middle of loading/creating
  const isClinicianReady =
    Boolean(clinicianId) && !loading && !isCreatingClinician && !authLoading;

  const contextValue = {
    user,
    setUser,
    userPreferences,
    setUserPreferences: useCallback(newPreferences => {
      setUserPreferences(normalizePreferences(newPreferences));
    }, []),
    preferencesLoading: authLoading || loading || isCreatingClinician,
    refreshUserPreferences: refreshClinician,
    preferencesError: error,
    onboardingCompleted,
    completeOnboarding,
    isClinicianReady,
    updateUserTheme,
    updateUserLanguage,
    updateUserTimezone,
    updateUserLengthUnit,
    updateUserWeightUnit,
    updateFloatSensitivity,
    updateKeyParameters,
    dateSettings: (() => {
      if (ENABLE_SERVER_DATE_SETTINGS) {
        const displaySettings = userPreferences?.display_settings || {};
        return {
          locale: displaySettings.date_locale ?? DEFAULT_DATE_SETTINGS.locale,
          format: displaySettings.date_format ?? DEFAULT_DATE_SETTINGS.format,
          source: "preferences",
        };
      }

      return {
        ...DEFAULT_DATE_SETTINGS,
        source: "client-default",
      };
    })(),
    dateSettingsIsClientOnly: !ENABLE_SERVER_DATE_SETTINGS,
  };

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
